<template>
  <div class="timerNumbers">
    <div class="number" :class="[blinkClass, timerNumberClass, blankSecNudge]">
      <span ref="$mins" class="mins">{{ bigText }}</span
      ><span v-if="hasSecs" class="secs">{{ smallText }}</span>
    </div>
    <div class="unit">
      <span class="min">{{ units }}</span
      ><span v-if="hasEach" class="each"> each</span>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import _ from 'underscore';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { usePhaseStore } from '@/store/pinia/phase';
import { useTimeStore } from '@/store/pinia/time';
import { useUsersStore } from '@/store/pinia/users';
import { useTimeDisplay } from '@/composables/useTimeDisplay';
import { splitDuration, asUnit } from '@/resources/time-helpers';
import { useUiAnimations } from '@/composables/useUiAnimations';

const props = defineProps({
  type: { type: String, default: 'phase' },
});

const { remaining, setting, settingTime: rawSettingTime } = useTimeDisplay({ type: props.type });
const { hiddenUpdate } = useUiAnimations();
const sidebarStore = useSidebarStore();
const phaseStore = usePhaseStore();
const timeStore = useTimeStore();
const usersStore = useUsersStore();
const { current: currentSidebar } = storeToRefs(sidebarStore);
const { currentPhase, preparing } = storeToRefs(phaseStore);
const { addingLocalTime, hasInteractedWithTimer } = storeToRefs(timeStore);
const { iAmModerator } = storeToRefs(usersStore);

const bigText = ref(0);
const $mins = ref(null);
const settingTime = ref(0);

const hasEach = computed(() => setting.value && ['checkIn', 'checkOut'].includes(currentPhase.value));
const hasSecs = computed(() => (props.type !== 'meetingSetter' ? true : false));

function paddedNumber(number, soloZero) {
  if (soloZero) {
    return number < 10 && number !== 0 ? '0' + number : number;
  } else {
    return number < 10 ? '0' + number : number;
  }
}

const settingSecs = computed(() => {
  const sec = splitDuration(settingTime.value * 1000, 'secs');
  return paddedNumber(sec, true);
});

const rawMinsText = computed(() => {
  let time;
  if (preparing.value) {
    time = preparing.value * 1000;
  } else {
    time = setting.value ? settingTime.value * 1000 : remaining.value;
  }

  let mins;
  let prefix = '';

  let { minutes, hours } = asUnit(time);

  if (props.type === 'meetingSetter') {
    if (minutes <= 90) {
      mins = Math.floor(minutes);
    } else {
      mins = Math.round(hours * 10) / 10;
      if (mins % 1 === 0) {
        mins = mins + '.0';
      }
    }
  } else if (minutes < 1 && setting.value && 1 === 2) {
    mins = Math.round(minutes);
  } else {
    mins = Math.floor(minutes);
  }

  mins = prefix + mins;

  return mins;
});

const smallText = computed(() => {
  let sec;
  if (preparing.value) {
    sec = splitDuration(preparing.value * 1000, 'secs');
  } else {
    sec = setting.value ? settingSecs.value : splitDuration(remaining.value, 'secs');
  }
  sec = sec || 0;

  if (setting.value && parseInt(sec) < 10) {
    return '';
  } else {
    return ':' + paddedNumber(sec);
  }
});

const units = computed(() => {
  if (props.type === 'meetingSetter') {
    return asUnit(settingTime.value, 'minutes') <= 90 ? 'mins' : 'hours';
  } else {
    const time = setting.value ? settingTime.value : remaining.value;
    let each = ['checkIn', 'checkOut'].includes(currentSidebar.value.phase) && setting.value ? ' each' : '';
    let min = Math.round(asUnit(time, 'minutes') * 10) / 10 === 1 ? 'min' : 'mins';
    return min + each;
  }
});

const blankSecNudge = computed(() => (smallText.value === '' ? 'blankSecNudge' : false));
const timerNumberClass = computed(() => 'digit-' + (isNaN(rawMinsText.value) ? 0 : rawMinsText.value));
const blinkClass = computed(() => (!hasInteractedWithTimer.value[props.type] && iAmModerator.value && props.type === 'meetingSetter' ? 'blink' : ''));

const minChange = _.debounce(function (newValue) {
  if (bigText.value !== newValue && $mins.value) {
    hiddenUpdate(
      $mins.value,
      () => {
        bigText.value = isNaN(rawMinsText.value) ? 0 : rawMinsText.value;
      },
      'flip',
    );
  }
}, 500);

onMounted(() => {
  nextTick(() => {
    updateSettingTime(rawSettingTime.value);
  });
});

watch(
  rawMinsText,
  (nv, ov) => {
    if (nv === ov) {
      return false;
    }
    if (setting.value) {
      bigText.value = isNaN(nv) ? 0 : nv;
    } else if (addingLocalTime.value) {
      bigText.value = isNaN(nv) ? 0 : nv;
      timeStore.setAddingLocalTime(false);
    } else {
      minChange(nv);
    }
  },
  {
    immediate: true,
  },
);

function updateSettingTime(nv) {
  settingTime.value = nv;
}
const debouncedUpdateSettingTime = _.debounce(updateSettingTime, 200);
watch(
  rawSettingTime,
  (nv) => {
    debouncedUpdateSettingTime(nv);
  },
  {
    immediate: true,
  },
);
</script>
<style scoped lang="scss">
.timerNumbers {
  .mins {
    @include type-size('massive');
    font-weight: 100;
    display: inline-block;
    color: var(--timer-minutes);
  }

  .blink {
    animation: timerBlink 2s infinite 5s;
  }

  .digit-1 .mins,
  .digit-4 .mins {
    margin-left: -0.05em;
  }
  .digit-2 .mins {
    margin-left: 0.03em;
  }
  .digit-3 .mins {
    margin-left: 0.04em;
  }
  .digit-5 .mins {
    margin-left: 0.01em;
  }
  .digit-10 .mins,
  .digit-15 .mins {
    margin-left: 0.05em;
  }

  .blankSecNudge {
    margin-left: 0.4em;
  }
}
</style>
